import { useLocation, useParams } from "react-router-dom"
import Selection, { SelectionItem, SubNavItem, SiteLink } from "@hydrologiq/shared/selection/Selection"
import { useSystemInfo } from "../../providers/helpers/useSystem"
import { useSystemViewData } from "../../providers/helpers/useSystem"
import { useRenameSystem } from "../../hooks/useRenameSystem"
import { pages } from "./pages"
import { routing } from "./routing"
import { useRoute } from "@hydrologiq/shared/providers/helpers/useRoute"
import { useGetFeature } from "@hydrologiq/shared/providers/helpers/useFeature"
import { useIsSectionEnabled } from "@hydrologiq/shared/providers/SectionFeatures"

function AssetsSelection({ children }: { children?: React.ReactNode }) {
  const { systemId } = useParams()
  const info = useSystemInfo()
  const viewData = useSystemViewData()
  const { renameSystem, renaming } = useRenameSystem(systemId ?? "")

  // The hardcoded demo system has no editable backing record — keep it read-only.
  const isDemoSystem = systemId === "848bd52d-c1cb-4013-8386-4bcb2b7e13a3"
  const route = useRoute()
  const location = useLocation().pathname
  const installationForecastEnabled = useGetFeature("installation_forecast")
  const hideReports = useGetFeature("hide_reports_system")
  const isCustomersEnabled = useIsSectionEnabled("customers")

  // Create parents for breadcrumb navigation
  const sectionPages = pages(location, systemId !== undefined)
  // Create sub-navigation items from sectionPages
  const subNavItems: SubNavItem[] = Object.entries(sectionPages.pages).filter(([key, value]) => {
    if (key === "forecasting") {
      return installationForecastEnabled
    }
    if (key === "reporting") {
      return !hideReports
    }
    return true
  }).map(([key, value]) => ({
    name: value,
    href: routing(route, location, systemId, false, key),
    disabled: (() => {
      if (["monitoring", "reporting", "forecasting"].includes(key)) {
        // If monitoring was previously activated (has timescale fields, installations,
        // or nodes with monitoring assignments), keep all enabled so historical
        // monitoring data remains accessible after a connection is deactivated/removed.
        const hasMonitoringHistory = (viewData?.system?.timescaleFields?.length ?? 0) > 0
          || (viewData?.installations?.length ?? 0) > 0
          || (viewData?.isolatedNodes?.some((n: any) => n.assignment?.monitoringInstanceId) ?? false)
          || (viewData?.installations?.some((g: any) => g.nodes?.some((n: any) => n.assignment?.monitoringInstanceId)) ?? false)
        return !hasMonitoringHistory && !info?.grouped
      }
      return false
    })(),
  }))

  // Create parents for breadcrumb navigation
  const parents: SelectionItem[] = []

  // Build site link from system view data
  const siteLink: SiteLink | undefined = (() => {
    const systemInfo = viewData?.system
    if (!systemInfo?.customerId || !systemInfo?.siteId || !systemInfo?.siteName) {
      return undefined
    }
    return {
      name: systemInfo.siteName,
      url: `/customers/${systemInfo.customerId}/sites/${systemInfo.siteId}`,
    }
  })()

  return (
    <Selection
      sectionBase="systems"
      sectionHeading="Systems"
      item={
        info && systemId
          ? {
              id: systemId,
              name: info.name,
              onRename: isDemoSystem ? undefined : renameSystem,
              renaming: isDemoSystem ? undefined : renaming,
            }
          : undefined
      }
      parents={parents}
      subNavItems={subNavItems}
      subNavHome={sectionPages.defaultPage}
      siteLink={siteLink}
      siteLinkDeLink={!isCustomersEnabled}
    >
      {children}
    </Selection>
  )
}

export default AssetsSelection
